import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Define the structure of customer node definitions
export interface CustomerNodeDefinition {
    customer_node_definition_id: number;
    master_node_type: string;
    name: string;
    config: {
        fields: {
            name: string;
            Value: any;
            editable: boolean;
        }[];
    };
    created_at?: string;
    updated_at?: string;
    node_type_fields?: NodeTypeField[]; // Added for `node_type_fields`
}

export interface NodeTypeField {
    name: string;
    type: string;
    Default?: any; // Optional field for default values
    JSONPath?: string; // Optional JSONPath for fetching dropdown options
    ValuesApi?: string; // Optional API endpoint for fetching dropdown values
}


interface PaginationState {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
}

interface CustomerNodeDefinitionState {
    items: CustomerNodeDefinition[]; // The actual data
    pagination: PaginationState; // Pagination-related information
    status: string | null; // Loading or error state
    search: string; // Search term
    sortBy: string; // Field to sort by
    sortOrder: 'asc' | 'desc'; // Sorting direction
    error: string | null; // Error message
}

const initialState: CustomerNodeDefinitionState = {
    items: [],
    pagination: {
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 100,
    },
    status: null,
    search: '',
    sortBy: 'customer_node_definition_id', // Default sort field
    sortOrder: 'asc', // Default sort order
    error: null,
};

export interface FetchParams {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
}

// Fetch customer node definitions with pagination, sorting, and search
export const fetchCustomerNodeDefinitions = createAsyncThunk(
    'customerNodeDefinitions/fetchCustomerNodeDefinitions',
    async (params: FetchParams) => {
        const { page, limit, sortBy, sortOrder, search } = params;
        const response = await api.get(
            `/customer_node_definitions?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}&search=${search || ''}`
        );
        return response.data;
    }
);

export const fetchCustomerNodeDefinitionById = createAsyncThunk<
    CustomerNodeDefinition, // The type of the payload the thunk resolves to
    number,                 // The type of the argument the thunk accepts
    { rejectValue: string } // Optional type for rejected value
>(
    'customerNodeDefinitions/fetchCustomerNodeDefinitionById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/customer_node_definitions/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue('Failed to fetch customer node definition');
        }
    }
);


// Create a new customer node definition
export const createCustomerNodeDefinition = createAsyncThunk(
    'customerNodeDefinitions/createCustomerNodeDefinition',
    async (definition: Omit<CustomerNodeDefinition, 'customer_node_definition_id'>) => {
        const response = await api.post('/customer_node_definitions', definition);
        return response.data;
    }
);

// Update an existing customer node definition
export const updateCustomerNodeDefinition = createAsyncThunk(
    'customerNodeDefinitions/updateCustomerNodeDefinition',
    async (definition: CustomerNodeDefinition) => {
        const response = await api.put(`/customer_node_definitions/${definition.customer_node_definition_id}`, definition);
        return response.data;
    }
);

// Delete a single customer node definition by ID
export const deleteCustomerNodeDefinition = createAsyncThunk(
    'customerNodeDefinitions/deleteCustomerNodeDefinition',
    async (customer_node_definition_id: number) => {
        await api.delete(`/customer_node_definitions/${customer_node_definition_id}`);
        return customer_node_definition_id;
    }
);

// Delete multiple customer node definitions by IDs
export const deleteCustomerNodeDefinitions = createAsyncThunk(
    'customerNodeDefinitions/deleteCustomerNodeDefinitions',
    async (customerNodeDefinitionIds: number[]) => {
        await api.delete('/customer_node_definitions', { data: { ids: customerNodeDefinitionIds } });
        return customerNodeDefinitionIds;
    }
);

const customerNodeDefinitionsSlice = createSlice({
    name: 'customerNodeDefinitions',
    initialState,
    reducers: {
        setSortOrder: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
        },
        setSearch: (state, action) => {
            state.search = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomerNodeDefinitions.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchCustomerNodeDefinitions.fulfilled, (state, action) => {
                const { pagination, data } = action.payload;
                state.items = data;
                state.pagination.totalItems = pagination.totalItems;
                state.pagination.totalPages = pagination.totalPages;
                state.pagination.pageSize = pagination.pageSize;

                if (pagination.currentPage > pagination.totalPages) {
                    state.pagination.currentPage = 1;
                } else {
                    state.pagination.currentPage = pagination.currentPage;
                }
            })
            .addCase(fetchCustomerNodeDefinitions.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'An error occurred';
            })
            .addCase(fetchCustomerNodeDefinitionById.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchCustomerNodeDefinitionById.fulfilled, (state, action) => {
                const updatedItem = action.payload;
                const combinedFields = updatedItem.node_type_fields.map((field) => ({
                    ...field,
                    ...updatedItem.config.fields.find((f) => f.name === field.name), // Merge matching config fields
                }));

                updatedItem.config.fields = combinedFields;

                const index = state.items.findIndex(
                    (definition) => definition.customer_node_definition_id === updatedItem.customer_node_definition_id
                );
                if (index !== -1) {
                    state.items[index] = updatedItem;
                } else {
                    state.items.push(updatedItem);
                }
                state.status = null;
            })

            .addCase(fetchCustomerNodeDefinitionById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || 'Failed to fetch customer node definition';
            })
            .addCase(createCustomerNodeDefinition.fulfilled, (state, action) => {
                state.items.push(action.payload);
            })
            // Deep merge fields during an update
            .addCase(updateCustomerNodeDefinition.fulfilled, (state, action) => {
                const index = state.items.findIndex(
                    (definition) => definition.customer_node_definition_id === action.payload.customer_node_definition_id
                );
                if (index !== -1) {
                    state.items[index].config.fields = action.payload.config.fields; // Replace or deeply merge as needed
                }
            })

            .addCase(deleteCustomerNodeDefinition.fulfilled, (state, action) => {
                state.items = state.items.filter(
                    (definition) => definition.customer_node_definition_id !== action.payload
                );
            })
            .addCase(deleteCustomerNodeDefinitions.fulfilled, (state, action) => {
                state.items = state.items.filter(
                    (definition) => !action.payload.includes(definition.customer_node_definition_id)
                );
            });
    },
});

export const { setSortOrder, setSearch } = customerNodeDefinitionsSlice.actions;

export default customerNodeDefinitionsSlice.reducer;